import {
  add as amplitudeAdd,
  identify as amplitudeIdentify,
  init as amplitudeInit,
  reset as amplitudeReset,
  setOptOut as amplitudeSetOptOut,
  setUserId as amplitudeSetUserId,
  track as amplitudeTrack,
  Identify as AmplitudeIdentify,
} from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

const API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY || 'a884a98dee3009d2b621fc438e97ed9e';

let initialised = false;
let pluginRegistered = false;
let consentGranted = false;
if (typeof window !== 'undefined') {
  try {
    const stored = JSON.parse(localStorage.getItem('pebbledash_cookies'));
    if (stored && stored.analytics === true) {
      consentGranted = true;
    }
  } catch (e) {
    consentGranted = false;
  }
}

function pruneUndefined(value) {
  if (Array.isArray(value)) {
    return value.map((item) => pruneUndefined(item)).filter((item) => item !== undefined);
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      const cleaned = pruneUndefined(val);
      if (cleaned !== undefined) acc[key] = cleaned;
      return acc;
    }, {});
  }
  return value === undefined ? undefined : value;
}

function ensureInit() {
  if (initialised) return;
  if (!consentGranted) return;
  if (typeof window === 'undefined') return;

  if (!pluginRegistered) {
    amplitudeAdd(sessionReplayPlugin({ sampleRate: 1 }));
    pluginRegistered = true;
  }

  amplitudeInit(API_KEY, {
    autocapture: true,
    serverZone: 'EU',
  });
  amplitudeSetOptOut(false);
  initialised = true;
}

export function initAmplitude() {
  ensureInit();
}

export function setAmplitudeConsent(enabled) {
  if (typeof window === 'undefined') return;
  consentGranted = !!enabled;

  if (consentGranted) {
    ensureInit();
  } else if (!initialised) {
    return;
  }
  try {
    if (consentGranted) {
      if (initialised) {
        amplitudeSetOptOut(false);
      }
    } else {
      amplitudeSetOptOut(true);
      amplitudeReset();
      initialised = false;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Amplitude] consent toggle failed', { enabled, error });
    }
  }
}

export function identifyAmplitudeUser(userId, properties = {}) {
  if (typeof window === 'undefined' || !userId || !consentGranted) return;
  ensureInit();
  try {
    if (!initialised) return;
    amplitudeSetUserId(userId);
    const cleaned = pruneUndefined(properties);
    if (cleaned && Object.keys(cleaned).length > 0) {
      const identifyUpdate = new AmplitudeIdentify();
      Object.entries(cleaned).forEach(([key, value]) => {
        if (value !== undefined) {
          identifyUpdate.set(key, value);
        }
      });
      amplitudeIdentify(identifyUpdate);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Amplitude] identify failed', { userId, properties, error });
    }
  }
}

export function resetAmplitudeIdentity() {
  if (typeof window === 'undefined') return;
  if (!initialised) return;
  try {
    amplitudeReset();
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Amplitude] reset failed', { error });
    }
  }
}

export function trackAmplitudeEvent(eventName, eventProperties = {}) {
  if (typeof window === 'undefined' || !consentGranted) return;
  ensureInit();
  if (!initialised) return;
  try {
    const cleaned = pruneUndefined(eventProperties);
    amplitudeTrack(eventName, cleaned);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Amplitude] track failed', { eventName, eventProperties, error });
    }
  }
}

export function trackPageView({ path, search = '', title }) {
  trackAmplitudeEvent('page_view', {
    path,
    search,
    title,
  });
}

export function trackCalculatorRun(calculator, { projectId = null, inputs = null, resultSummary = null } = {}) {
  const props = {};
  props.calculator = calculator;
  if (projectId) props.project_id = projectId;
  if (inputs) props.inputs = inputs;
  if (resultSummary) props.result_summary = resultSummary;
  trackAmplitudeEvent('calculator_run', props);
}
